import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  type NotFoundRouteProps,
} from '@tanstack/react-router'
import { Shell } from '@/components/shell'
import { Button } from '@/components/ui/button'
import { CallRoute } from '@/routes/call'
import { HistoryRoute } from '@/routes/history'
import { HomeRoute } from '@/routes/home'
import { LobbyRoute } from '@/routes/lobby'
import { SessionDetailRoute } from '@/routes/session-detail'

/**
 * Code-based routes rather than the file-route generator.
 *
 * The generator writes a `routeTree.gen.ts` that has to be regenerated and
 * kept in sync. Four routes do not need that, and a build with no codegen step
 * is a build that cannot produce a stale artifact. Same call the operator
 * console made.
 */

const rootRoute = createRootRoute({
  component: Shell,
  notFoundComponent: NotFound,
})

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomeRoute,
})

/**
 * The call is addressed by JOIN CODE, not by session id.
 *
 * That is what someone types or pastes from a message, and it is the only one
 * of the two identifiers a human can carry. The session id is derived from it
 * server-side.
 */
const callRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/call/$code',
  component: CallRoute,
})

/**
 * The waiting room, addressed by APPOINTMENT SLUG.
 *
 * A third identifier in the URL space, and the one that is publishable before
 * anything exists: `/call/$code` needs a room, `/session/$sessionId` needs a
 * finished one, and this needs neither. Short path (`/j/`) because it is the
 * only URL in this app that gets pasted into a calendar invite.
 */
const lobbyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/j/$slug',
  component: LobbyRoute,
})

const historyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/history',
  component: HistoryRoute,
})

/**
 * The recording is addressed by SESSION ID, not by join code.
 *
 * The opposite choice from `/call/$code`, on purpose: a join code is a
 * short-lived, reusable, guessable handle for a live room, and a link to a
 * finished recording should not be any of those three things.
 */
const sessionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/session/$sessionId',
  component: SessionDetailRoute,
})

const routeTree = rootRoute.addChildren([homeRoute, callRoute, lobbyRoute, historyRoute, sessionRoute])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Scroll position is restored per route. Without it, opening a session from
  // halfway down the history list lands you halfway down the session page.
  scrollRestoration: true,
  scrollToTopSelectors: ['[data-scroll-restoration-id="app-scroll"]'],
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function NotFound(_props: NotFoundRouteProps) {
  return (
    <div className="mx-auto max-w-lg space-y-4 py-20 text-center">
      <p className="eyebrow">404</p>
      <h1 className="display text-3xl">There is nothing at this address.</h1>
      <p className="text-sm text-ink-muted">
        A join code is six characters. If you followed a link to a call, the room may already have
        ended.
      </p>
      <Button variant="primary" asChild className="mt-2">
        <Link to="/">Start a new call</Link>
      </Button>
    </div>
  )
}
